import { Injectable } from '@angular/core';

interface CacheEntry<T> {
	value: T;
	timestamp: number;
	pending?: Promise<T>;
}

@Injectable({
	providedIn: 'root',
})
export class CacheService {
	private cache: { [key: string]: CacheEntry<any> } = {};
	private readonly DEFAULT_DURATION = 5 * 60 * 1000;
	private readonly PENDING_TIMEOUT = 10000;

	get<T>(key: string, fetcher: () => Promise<T>, fallback: T): Promise<T> {
		const entry = this.cache[key];
		const now = Date.now();

		// Valid cache hit - only if we have a real (non-null) value
		if (entry && entry.value != null && now - entry.timestamp < this.DEFAULT_DURATION) {
			return Promise.resolve(entry.value);
		}

		// Request in progress
		if (entry?.pending) {
			return this.withPendingTimeout(entry.pending, this.PENDING_TIMEOUT, fallback);
		}

		// Start new request
		const pending = this.executeFetcher(key, fetcher, fallback);
		this.cache[key] = {
			value: null as any,
			timestamp: now,
			pending,
		};

		return pending;
	}

	private withPendingTimeout<T>(promise: Promise<T>, ms: number, fallbackValue: T): Promise<T> {
		const timeout = new Promise<never>((_, reject) =>
			setTimeout(() => reject(new Error('pending timeout')), ms)
		);

		return Promise.race([promise, timeout]).catch(() => fallbackValue);
	}

	private executeFetcher<T>(key: string, fetcher: () => Promise<T>, fallback: T): Promise<T> {
		return fetcher()
			.then(result => {
				// Always cache the real result (even if it's null, but we prefer not to hit on null)
				this.cache[key] = {
					value: result,
					timestamp: Date.now(),
				};
				return result;
			})
			.catch(() => {
				const fallbackValue = fallback;
				this.cache[key] = {
					value: fallbackValue,
					timestamp: Date.now(),
				};
				return fallbackValue;
			});
	}
}